import Message from '@/plugins/message';
import { useUserStore } from "@/stores/user";
import { ref, reactive, computed, watch, inject } from "vue";
import UserService from '@/services/user.service';
import type { PolicyList } from '@/services/user.service';
import type CyForm from '@/components/ui/cy-form.vue';
import { t } from '@/locale';
import { useRouter, useRoute } from 'vue-router';
interface LoginForm {
  cellphone: string;
  smsCode: string;
  imageCode: string;
}

const phoneCheck = /^[1][0-9]{10}$/;

export default function useLoginPageState() {
  const service = new UserService();
  const userStore = useUserStore();
  const router = useRouter();

  const visible = ref(false);
  const loginFormRef = ref<InstanceType<typeof CyForm>>();
  const loginForm = reactive<LoginForm>({
    cellphone: '',
    smsCode: '',
    imageCode: '',
  });
  const isValidCellphone = ref(false);

  const imgCodePopVisible = ref(false); // 是否需要图片验证码
  const smsCodePopVisible = ref(false);

  const isShowGetCodeButton = ref(true); // 是否显示获取验证码按钮
  const downCount = ref(60); // 倒计时秒数
  const downTimer = ref<any>(); // 定时器标识
  const imageCode = ref(''); // 图像验证码
  const isCheckPolicy = ref(false); // 是否勾选协议
  const loginLoading = ref(false);
  const policyList = ref<PolicyList>([]);
  const policyModelVisible = ref(false);
  const policyInfo = reactive<{
    policyName: string;
    policyContent: string;
  }>({
    policyContent: '',
    policyName: '',
  });
  const confirmPolicy = ref(false);

  const app = inject<{ reload: ()=> void; }>('app');
  // computed
  const isShowPolicy = computed(() => !!policyList.value.length);
  const smsStyle = computed(() => {
    return loginForm.smsCode ? {} : { 'transform': 'translateY(8px)' };
  })


  // watch
  watch(() => loginForm.cellphone, (newCellphone) => {
    if (!newCellphone) return;
    if (phoneCheck.test(newCellphone)) {
      isValidCellphone.value = true;
    } else {
      isValidCellphone.value = false;
    }
  }, { immediate: true })


  // function
  const showLoginModal = () => {
    loginForm.cellphone = '';
    loginForm.imageCode = '';
    loginForm.smsCode = '';
    clearValidate();
    isValidCellphone.value = false;
    imgCodePopVisible.value = false;
    smsCodePopVisible.value = false;
    isCheckPolicy.value = false;
    imageCode.value = '';
    isShowGetCodeButton.value = true;
    _getDefaultPolicy().then(() => {
      visible.value = true;
    }).finally(() =>{
      visible.value = true;
    });
  }

  // 获取默认的协议列表
  const _getDefaultPolicy = () => {
    return service.getDefaultPolicy().then(({ statusCode, data }) => {
      if (statusCode === 200 && data) {
        policyList.value = data || [];
        policyList.value = policyList.value.filter(v => v.code === 'USER_SERVICE_POLICY' || v.code === 'USER_PRIVACY_POLICY')
        return Promise.resolve();
      }
    }).finally(() => Promise.resolve());
  }

  // 清除验证作用
  const clearValidate = () => {
    loginFormRef.value?.clearValidate();
  }

  const authCodeLength = ref(4);
  // 获取手机验证码
  const getPhoneCode = () => {
    if (!isCheckPolicy.value && policyList.value.length) {
      confirmPolicy.value = true;
      return
    }
    clearValidate();
    if (!loginForm.cellphone) {
      loginFormRef.value?.setValidate('cellphone', t('loginModal.pleaseEnterCellPhone'));
      return;
    }
    if (!isValidCellphone.value) {
      loginFormRef.value?.setValidate('cellphone', t('loginModal.pleaseEnterGoodCellphone'));
      return;
    };
    if (imgCodePopVisible.value && !loginForm.imageCode) {
      Message.error({ message: t('loginModal.pleaseEnterImageCode') })
      return;
    }
    service.getPhoneCode(loginForm.cellphone, loginForm.imageCode).then(({ statusCode, comments, data }) => {
      if (statusCode === 200 || statusCode === 15012012) {
        if (imgCodePopVisible.value && !loginForm.imageCode) {
          Message.error({ message: comments })
        }
        authCodeLength.value = data?.authCodeLength;
        baseResponse(statusCode, comments);
      }
    }).catch(({ data }) => {
      const { statusCode, comments } = data;
      baseResponse(statusCode, comments);
    })
  };


  const baseResponse = (statusCode: number, comments: string) => {
    if (statusCode === 200) {
      smsCodePopVisible.value = true;
      imgCodePopVisible.value  = false;
      timeDown();
    } else if (statusCode === 1004) {
      imgCodePopVisible.value  = true;
      getImageCode();
    } else if (statusCode === 15012012) {
      imgCodePopVisible.value  = true;
      getImageCode();
    } else if (statusCode === 15012016) {
      getImageCode();
      loginForm.imageCode = '';
    } else if (statusCode === 1007) {
      imgCodePopVisible.value = true;
      getImageCode();
    } else if (statusCode === 15012018) {
      imgCodePopVisible.value = true;
      smsCodePopVisible.value = false;
      getImageCode();
      loginForm.imageCode = '';
    }
  }

  // 获取图形验证码
  const getImageCode = () => {
    service.getPicCode(loginForm.cellphone).then(({ statusCode, data }) => {
      if (statusCode == 200 && data) {
        imageCode.value = data.baseCode || '';
      }
    })
  }

  const timeDown = () => {
    const max_count = 60;
    if (!downTimer.value) {
      downCount.value = max_count;
      isShowGetCodeButton.value = false;
      downTimer.value = setInterval(() => {
        if (downCount.value > 0 && downCount.value <= max_count) {
          downCount.value -= 1;
        } else {
          isShowGetCodeButton.value = true;
          clearInterval(downTimer.value);
          downTimer.value = null;
        }
      }, 1000)
    }
  }

  const handleConfirmPolicyClose = () => {
    confirmPolicy.value = false;
  }

  const handleConfirmPolicyConfirm = () => {
    confirmPolicy.value = false;
    isCheckPolicy.value = true;
    login();
  }

  const login = () => {
    clearValidate();
    if (!loginForm.cellphone) {
      loginFormRef.value?.setValidate('cellphone', t('loginModal.pleaseEnterCellPhone'));
      return;
    }
    if (!isValidCellphone.value) {
      loginFormRef.value?.setValidate('cellphone', t('loginModal.pleaseEnterGoodCellphone'));
      return;
    };
    if (imgCodePopVisible.value && !loginForm.imageCode) {
      loginFormRef.value?.setValidate('imageCode', t('loginModal.pleaseEnterImageCode'));
      return;
    };
    if (!loginForm.smsCode) {
      loginFormRef.value?.setValidate('smsCode', t('loginModal.pleaseEnterSmsCode'));
      return;
    }
    // if (isShowPolicy.value && !isCheckPolicy.value) {
    //   loginFormRef.value?.setValidate('isCheckPolicy', t('loginModal.pleaseCheckPolicy'));
    //   return;
    // }

    // 协议check
    if (!isCheckPolicy.value && policyList.value.length) {
      confirmPolicy.value = true;
      return
    }

    // 所有验证走完 给到提示
    loginLoading.value = true;
    service.login(loginForm.cellphone, loginForm.smsCode).then((res) => {
      const { statusCode, data, comments } = res;
      if (statusCode === 200 && data) {
        userStore.login({
          ...data,
          rawCellphone: loginForm.cellphone,
        });
        handleClose();
      } else {
        Message.warning({ message: comments });
      }
    }).catch(() => {}).finally(() => {
      loginLoading.value = false;
    });

  }

  // 关闭登录弹窗
  const handleClose = () => {
    visible.value = false;
    smsCodePopVisible.value = false;
    imgCodePopVisible.value = false;
    if (window.location.href?.includes('user-center')) {
      router?.push({path:'/'});
    } else {
      app?.reload();
    }
  }

  // 关闭隐私弹窗
  const handleClosePolicyModel = () => {
    policyInfo.policyContent = '';
    policyInfo.policyName = '';
    policyModelVisible.value = false;
  }

  // 获取隐私内容
  const showPolicyModel = (id:string) => {
    service.getPolicyById(id).then((res) => {
      const { statusCode, data } = res;
      if (statusCode === 200) {
        Object.assign(policyInfo, data);
        policyModelVisible.value = true;
      }
    })
  };

  // 清除函数
  const clearForm = (type: 'cellphone' | 'smsCode' | 'imageCode') => {
    if (type === 'cellphone') {
      loginForm.cellphone = '';
    } else if (type === 'smsCode') {
      loginForm.smsCode = '';
    } else if (type === 'imageCode') {
      loginForm.imageCode = '';
    }
  }

  return {
    // state
    visible,
    loginForm,
    isShowPolicy,
    policyList,
    isCheckPolicy,
    isValidCellphone,
    isShowGetCodeButton,
    downCount,
    imgCodePopVisible,
    smsCodePopVisible,
    authCodeLength,
    imageCode,
    loginLoading,
    loginFormRef,
    policyModelVisible,
    policyInfo,
    smsStyle,
    confirmPolicy,
    // function
    showLoginModal,
    getPhoneCode,
    getImageCode,
    login,
    handleClose,
    handleClosePolicyModel,
    showPolicyModel,
    clearValidate,
    clearForm,
    handleConfirmPolicyClose,
    handleConfirmPolicyConfirm,
  };
};
